import { useEffect, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { countOffersAwaitingResponse } from '../utils/offerAwaitingResponse';

/**
 * How many open offers are waiting on the signed-in user right now.
 *
 * Drives the Trading badge in the top nav, the bottom tab bar, and the Offers
 * tile on the Trading hub (owner, 2026-11-02). Unlike the old bottom-tab
 * badge, which counted unread notifications and so cleared when the bell was
 * read, this clears per offer as it is answered, countered away, cancelled,
 * or expired, because it is computed from the offer documents themselves.
 *
 * Four live queries, the same shapes TradingHub already runs (so rules and
 * indexes are proven): trade offers to / from me and purchase offers where I
 * am responder / initiator, each limited to pending + countered. The sent
 * sides are needed because a counter puts the offer back in the sender's
 * court. The pure turn + 48h rule lives in utils/offerAwaitingResponse.js.
 *
 * The listeners are shared: however many components mount this hook, each
 * signed-in user has one set of four subscriptions, torn down when the last
 * subscriber unmounts or the user signs out.
 */

const STATUSES = ['pending', 'countered'];
const QUERY_SHAPES = [
  { kind: 'trade', coll: 'tradeOffers', field: 'fromUserId' },
  { kind: 'trade', coll: 'tradeOffers', field: 'toUserId' },
  { kind: 'purchase', coll: 'purchaseOffers', field: 'initiatorId' },
  { kind: 'purchase', coll: 'purchaseOffers', field: 'responderId' },
];

// One entry per uid: { count, docs: Map<key, offer>, unsubs, listeners: Set<fn> }
const shared = new Map();

function recompute(entry, uid) {
  const next = countOffersAwaitingResponse(Array.from(entry.docs.values()), uid);
  if (next === entry.count) return;
  entry.count = next;
  entry.listeners.forEach((fn) => fn(next));
}

function subscribe(uid, listener) {
  let entry = shared.get(uid);
  if (!entry) {
    entry = { count: 0, docs: new Map(), listeners: new Set(), unsubs: [] };
    shared.set(uid, entry);
    for (const shape of QUERY_SHAPES) {
      // A doc can only appear in one of the two queries for its collection
      // (from != to), but key by shape so a doc that moves between snapshots
      // is replaced rather than duplicated.
      const q = query(collection(db, shape.coll), where(shape.field, '==', uid), where('status', 'in', STATUSES));
      const unsub = onSnapshot(q, (snap) => {
        for (const d of entry.docs.keys()) if (d.startsWith(`${shape.coll}/${shape.field}/`)) entry.docs.delete(d);
        snap.docs.forEach((doc) => {
          entry.docs.set(`${shape.coll}/${shape.field}/${doc.id}`, { kind: shape.kind, id: doc.id, ...doc.data() });
        });
        recompute(entry, uid);
      }, (err) => {
        // Permission or network failure: fall back to "nothing waiting" rather
        // than a stuck stale number. The hub still shows its own counts.
        console.warn(`[offersAwaitingResponse] ${shape.coll}/${shape.field} listener failed:`, err?.message || err);
      });
      entry.unsubs.push(unsub);
    }
  }
  entry.listeners.add(listener);
  listener(entry.count);
  return () => {
    entry.listeners.delete(listener);
    if (entry.listeners.size === 0) {
      entry.unsubs.forEach((u) => { try { u(); } catch { /* already closed */ } });
      shared.delete(uid);
    }
  };
}

export function useOffersAwaitingResponse() {
  const [uid, setUid] = useState(() => auth.currentUser?.uid || null);
  const [count, setCount] = useState(0);

  useEffect(() => auth.onAuthStateChanged((user) => setUid(user?.uid || null)), []);

  useEffect(() => {
    if (!uid) { setCount(0); return undefined; }
    const unsubscribe = subscribe(uid, setCount);
    return unsubscribe;
  }, [uid]);

  // The 48h window closes on its own without a Firestore event, so re-count
  // every minute while mounted; the pure helper is cheap on a handful of docs.
  useEffect(() => {
    if (!uid) return undefined;
    const id = setInterval(() => { const e = shared.get(uid); if (e) recompute(e, uid); }, 60 * 1000);
    return () => clearInterval(id);
  }, [uid]);

  return count;
}

// Test-only: drop every shared subscription.
export function __resetOffersAwaitingResponseForTests() {
  shared.forEach((entry) => entry.unsubs.forEach((u) => { try { u(); } catch { /* noop */ } }));
  shared.clear();
}
